import { useState, useRef, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Badge } from '../../../components/ui/badge';
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, DollarSign, RotateCcw, Printer } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Bulk Cost-Price import — download a template pre-filled with every product's
 * current cost, fill in the "New Cost" column, upload, preview, then apply.
 * Only the cost_price field is affected; qty, selling_price and everything
 * else is untouched. Matches by Stock Code (supplier_sku).
 */
export default function CostPriceImportPanel() {
  const [pickedFile, setPickedFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run result
  const [applied, setApplied] = useState(null); // apply result
  const inputRef = useRef(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const totalDelta = useMemo(() => {
    if (!preview?.changes) return 0;
    return preview.changes.reduce((s, r) => s + (r.diff || 0), 0);
  }, [preview]);

  const handleDownloadTemplate = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/products/cost-template-xlsx`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : 'jsm-cost-price-template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadEntrySheet = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/api/auto-spares/products/cost-entry-sheet-pdf`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : 'jsm-cost-entry-sheet.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Cost entry sheet downloaded — print and hand to pricing staff');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    } finally {
      setBusy(false);
    }
  };

  const runImport = async (dryRun) => {
    if (!pickedFile) {
      toast.error('Pick a .xlsx file first');
      return;
    }
    setBusy(true);
    setApplied(null);
    try {
      const fd = new FormData();
      fd.append('file', pickedFile);
      fd.append('dry_run', dryRun ? 'true' : 'false');
      const res = await axios.post(
        `${API_URL}/api/auto-spares/products/import-cost-xlsx`,
        fd,
        { headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' } }
      );
      if (dryRun) {
        setPreview(res.data);
        toast.success(`Preview: ${res.data.changed} change${res.data.changed === 1 ? '' : 's'}, ${res.data.unchanged} unchanged, ${res.data.blank ?? 0} blank, ${res.data.skipped} errored`);
      } else {
        setApplied(res.data);
        setPreview(null);
        toast.success(`Applied — ${res.data.changed} cost price${res.data.changed === 1 ? '' : 's'} updated`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setPickedFile(null);
    setPreview(null);
    setApplied(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-emerald-400" /> Bulk Cost Price Import
          </h2>
          <p className="text-slate-400 text-sm">Update cost prices for many products at once. Nothing else on the products is touched.</p>
        </div>
        <Button
          onClick={handleReset}
          variant="outline"
          className="bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-700"
          data-testid="cost-import-reset-btn"
        >
          <RotateCcw className="h-4 w-4 mr-1" /> Reset
        </Button>
      </div>

      {/* Step 1 */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <span className="bg-cyan-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">1</span>
            Download Template
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-300">
            The template contains every product with its <span className="text-white font-semibold">Stock Code</span>, <span className="text-white font-semibold">Name</span>, <span className="text-white font-semibold">Category</span> and the <span className="text-white font-semibold">Current Cost</span> — filled in for reference. Enter the new cost price in the <span className="text-emerald-300 font-semibold">"New Cost (R)"</span> column and leave blanks where you don't want a change.
          </p>
          <Button
            onClick={handleDownloadTemplate}
            disabled={busy}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="cost-template-download-btn"
          >
            <Download className="h-4 w-4 mr-2" /> Download Cost Price Template (.xlsx)
          </Button>
          <div className="pt-2 border-t border-slate-700">
            <p className="text-xs text-slate-400 mb-2">
              Or print a paper worksheet showing only products still at cost=0, grouped by category with a blank COST column for pen-fill:
            </p>
            <Button
              onClick={handleDownloadEntrySheet}
              disabled={busy}
              variant="outline"
              className="border-amber-600 text-amber-300 hover:bg-amber-600/20"
              data-testid="cost-entry-sheet-btn"
            >
              <Printer className="h-4 w-4 mr-2" /> Print Cost Entry Sheet (PDF)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 2 */}
      <Card className="bg-slate-800/60 border-slate-700">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-white flex items-center gap-2">
            <span className="bg-cyan-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">2</span>
            Upload Completed Sheet
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(e) => { setPickedFile(e.target.files?.[0] || null); setPreview(null); setApplied(null); }}
            className="block w-full text-sm text-slate-200 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-amber-600 file:text-white hover:file:bg-amber-700 file:cursor-pointer"
            data-testid="cost-import-file-input"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => runImport(true)}
              disabled={busy || !pickedFile}
              className="bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-40"
              data-testid="cost-import-preview-btn"
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              {busy ? 'Working…' : 'Preview Changes'}
            </Button>
            <Button
              onClick={() => runImport(false)}
              disabled={busy || !preview || preview.changed === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-40"
              data-testid="cost-import-apply-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              {busy ? 'Applying…' : `Apply ${preview?.changed || 0} Change${(preview?.changed || 0) === 1 ? '' : 's'}`}
            </Button>
          </div>
          <p className="text-xs text-slate-500">
            Matching is by Stock Code. Blank "New Cost" cells are ignored so you can update only the products you need.
          </p>
        </CardContent>
      </Card>

      {/* Preview / Applied results */}
      {(preview || applied) && (
        <Card className="bg-slate-800/60 border-slate-700">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-white flex items-center gap-2">
              {applied ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                  Applied — {applied.changed} cost price{applied.changed === 1 ? '' : 's'} updated
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-5 w-5 text-amber-400" />
                  Preview
                </>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <div className="bg-slate-900 rounded p-3 border border-slate-700">
                <div className="text-[10px] text-slate-500 uppercase">Will Change</div>
                <div className="text-xl font-bold text-emerald-400" data-testid="cost-import-changed-count">
                  {(applied || preview).changed}
                </div>
              </div>
              <div className="bg-slate-900 rounded p-3 border border-slate-700">
                <div className="text-[10px] text-slate-500 uppercase">Unchanged</div>
                <div className="text-xl font-bold text-slate-300">{(applied || preview).unchanged}</div>
              </div>
              <div className="bg-slate-900 rounded p-3 border border-slate-700">
                <div className="text-[10px] text-slate-500 uppercase">Blank Rows</div>
                <div className="text-xl font-bold text-slate-500" data-testid="cost-import-blank-count">
                  {(applied || preview).blank ?? 0}
                </div>
              </div>
              <div className="bg-slate-900 rounded p-3 border border-slate-700">
                <div className="text-[10px] text-slate-500 uppercase">Errored</div>
                <div className="text-xl font-bold text-amber-400" data-testid="cost-import-skipped-count">
                  {(applied || preview).skipped}
                </div>
              </div>
              <div className="bg-slate-900 rounded p-3 border border-slate-700">
                <div className="text-[10px] text-slate-500 uppercase">Net Δ / Unit (sum)</div>
                <div className={`text-xl font-bold ${totalDelta >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {totalDelta >= 0 ? '+' : ''}{R(totalDelta)}
                </div>
              </div>
            </div>

            {((applied || preview).errors || []).length > 0 && (
              <details className="bg-rose-950/40 border border-rose-700 rounded p-3 text-sm text-rose-300">
                <summary className="cursor-pointer flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {(applied || preview).errors.length} row{(applied || preview).errors.length === 1 ? '' : 's'} skipped
                </summary>
                <ul className="ml-4 mt-1 list-disc">
                  {(applied || preview).errors.map((er, i) => (
                    <li key={i}>Row {er.row}: {er.error}</li>
                  ))}
                </ul>
              </details>
            )}

            <div className="rounded border border-slate-700 max-h-[60vh] overflow-y-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-slate-900 z-10">
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Stock Code</TableHead>
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead>
                    <TableHead className="text-slate-400 text-right">Old Cost</TableHead>
                    <TableHead className="text-slate-400 text-right">New Cost</TableHead>
                    <TableHead className="text-slate-400 text-right">Δ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {((applied || preview).changes || []).length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-6">
                        No cost changes detected.
                      </TableCell>
                    </TableRow>
                  ) : (
                    (applied || preview).changes.map((row, i) => (
                      <TableRow key={row.product_id || i} className="border-slate-800 hover:bg-slate-800/40" data-testid={`cost-change-row-${row.supplier_sku}`}>
                        <TableCell className="text-slate-300 font-mono text-xs">{row.supplier_sku}</TableCell>
                        <TableCell className="text-white text-sm">{row.name}</TableCell>
                        <TableCell className="text-slate-400 text-xs">
                          <Badge className="bg-slate-900 border border-slate-700 text-slate-300">{row.category}</Badge>
                        </TableCell>
                        <TableCell className="text-right text-slate-400 font-mono text-xs">{R(row.old_cost)}</TableCell>
                        <TableCell className="text-right text-emerald-300 font-mono text-xs font-semibold">{R(row.new_cost)}</TableCell>
                        <TableCell className={`text-right font-mono text-xs font-semibold ${row.diff > 0 ? 'text-amber-400' : row.diff < 0 ? 'text-rose-400' : 'text-slate-400'}`}>
                          {row.diff > 0 ? '+' : ''}{R(row.diff)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
